import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { result } from 'lodash';
import {
  ClaimReference,
  httpSearchReferencesByClaim
} from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpSearchReferencesByClaim';
import { css } from 'styled-components';

export type JudgmentCounts = {
  pro: number;
  con: number;
  neutral: number;
  all: number;
};

export type UseReferencesByClaimResult = {
  references: ClaimReference[];
  judgmentCounts: JudgmentCounts;
};

export const judgmentCountsDefault: JudgmentCounts = {
  pro: 0,
  con: 0,
  neutral: 0,
  all: 0
} as const;

export const judgmentCountsProCss = css`
  color: ${({ theme }) => theme.colors.secondaryColorMain};
`;

export const judgmentCountsConCss = css`
  color: ${({ theme }) => theme.colors.accentRedDark};
`;

export const judgmentCountsNeutralCss = css`
  color: ${({ theme }) => theme.colors.accentYellowDark};
`;

export const getProPercentage = (judgmentCounts: JudgmentCounts): number => {
  const { pro, all } = judgmentCounts;

  return all > 0 ? (pro / all) * 100 : 0;
};

export const isPro = (judgmentCounts: JudgmentCounts): boolean =>
  getProPercentage(judgmentCounts) >= 70;

export const isNeutral = (judgmentCounts: JudgmentCounts): boolean => {
  const proPercentage = getProPercentage(judgmentCounts);

  return proPercentage >= 30 && proPercentage < 70;
};

export const isCon = (judgmentCounts: JudgmentCounts): boolean =>
  getProPercentage(judgmentCounts) < 30;

type ReferencesByClaimInput = {
  claim: string;
};

type ReferencesByClaimMutationOptions = Omit<
  UseMutationOptions<UseReferencesByClaimResult, unknown, ReferencesByClaimInput>,
  'mutationFn'
>;

export const useReferencesByClaim = (options?: ReferencesByClaimMutationOptions) => {
  return useMutation<UseReferencesByClaimResult, unknown, ReferencesByClaimInput>({
    mutationFn: async (input: ReferencesByClaimInput) => {
      const claimReferences = await httpSearchReferencesByClaim.callEndpoint(input.claim);

      const judgmentCounts: JudgmentCounts = claimReferences.reduce(
        (acc, chunk) => {
          acc[chunk.label]++;
          acc.all++;
          return acc;
        },
        { pro: 0, con: 0, neutral: 0, all: 0 }
      );

      return { references: claimReferences, judgmentCounts };
    },
    ...options
  });
};
